import {
  AfterViewInit,
  Component,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  firstValueFrom,
} from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { AlfredClientService } from '../../../../alfred-client.service';
import { MultipleChoiceQuestionAnswer } from '../../../../models/answer/multiple-choice-answer.model';
import { Choice } from '../../../../models/questions/choice.model';
import { MultipleChoiceQuestion } from '../../../../models/questions/multiple-choice-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { ChoiceOrdering } from '../../../../shared/enums/choice-ordering.enum';
import {
  QuestionAnswer,
  QuestionControlType,
} from '../question-type.interface';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import {
  IonCheckbox,
  IonRadioGroup,
  IonRadio,
} from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';
import { DisplayMathPipe } from '@lyceo/shared/question-display/feature-main';

/**
 * The multiple choice question type has multiple options.
 * Based on the question.isMultipleChoice property the user
 * can either select a single options or multiple. The answer
 * is always returned as an array.
 *
 * @example
 * <lib-multiple-choice-question
 *                               [question]="questionData.question"
 *                               [formControl]="questionFormCtrl">
 * </lib-multiple-choice-question>
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-multiple-choice-question',
  templateUrl: './multiple-choice-question.component.html',
  styleUrls: ['./multiple-choice-question.component.scss'],
  imports: [
    QuestionDisplayComponent,
    IonCheckbox,
    IonRadioGroup,
    IonRadio,
    AsyncPipe,
    DisplayMathPipe,
  ],
  host: {
    '(window:keydown)': 'selectOption($event)',
  },
})
export class MultipleChoiceQuestionComponent
  implements AfterViewInit, OnLoaded, QuestionControlType
{
  public alfredClientService: AlfredClientService = inject(AlfredClientService);

  /**
   * Array of characters so we can map the index of a choice to a character.
   */
  public readonly Alfabet: string[] = 'abcdefghijklmnopqrstuvwxyz'.split('');

  /**
   * questions data used to created the multiple choice question.
   */
  public set question(question: MultipleChoiceQuestion) {
    if (
      (!(question && question.choices) || question.choices.length === 0) &&
      this.alfredClientService.isDirectHandleMode()
    ) {
      question.choices = [
        {
          value: '<i>[hier komen de antwoordmogelijkheden]</i>',
          position: 0,
        },
      ];
    }

    // Order the choices if necessary.
    switch (question.ordering) {
      case ChoiceOrdering.ALPHABETICAL:
        question.choices = question.choices.sort((a: Choice, b: Choice) =>
          a.value.localeCompare(b.value),
        );
        break;
      case ChoiceOrdering.BY_POSITION:
        question.choices = question.choices.sort(
          (a: Choice, b: Choice) => a.position - b.position,
        );
        break;
      case ChoiceOrdering.RANDOM: // Do nothing
        break;
    }

    this._question = question;
  }

  /**
   * questions data used to created the multiple choice question.
   */
  public _question!: MultipleChoiceQuestion;

  protected _disabled: boolean = false;

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: MultipleChoiceQuestionAnswer | null) {
    if (!answer) {
      this.value$.next([]);
      return;
    }

    if (this._question.isMultipleChoice) {
      for (const item of answer) {
        this.onMultiSelect(item);
      }
    } else {
      this.onSingleSelect(answer[0]);
    }
  }

  /**
   * The current answer of the question.
   */
  public get answer(): MultipleChoiceQuestionAnswer {
    return this.value$.getValue();
  }

  /**
   * Return A single choice.
   */
  public get singleChoiceAnswer$(): Observable<string> {
    return this.value$.pipe(
      map((choices) => {
        return choices.length > 0 ? choices[0] : (undefined as never);
      }),
    );
  }

  /**
   * The current answer of the question.
   */
  public value$: BehaviorSubject<MultipleChoiceQuestionAnswer> =
    new BehaviorSubject<MultipleChoiceQuestionAnswer>([]);

  /**
   * Boolean which is true when there isn't any user input yet.
   */
  private pristine: boolean = true;

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): QuestionAnswer {
    return this.value$.getValue();
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<QuestionAnswer> {
    // skipping the initial value of the behavior u
    return this.value$.asObservable();
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.value$.getValue().length > 0;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.value$.pipe(map((value) => value.length > 0));
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  /**
   * Return the available hotkeys based on the available options.
   */
  public hotkeys(): string[] {
    return this.Alfabet.slice(0, this._question.choices.length);
  }

  /**
   * Only listen to available hotkeys.
   */
  public selectOption(event: KeyboardEvent): void {
    if (this.alfredClientService.isDirectHandleMode()) {
      //disable hotkey in directHandleMode
      return;
    }
    // Only listen to available hotkeys.
    if (!this.hotkeys().includes(event.key)) {
      return;
    }

    const index: number = this.Alfabet.indexOf(event.key);
    const value: string = this._question.choices[index].value;

    if (this._question.isMultipleChoice) {
      this.onMultiSelect(value);
    } else {
      this.onSingleSelect(value);
    }
  }

  /**
   * Either select or de-select the provided option.
   */
  public onMultiSelect(value: string): void {
    if (!this.isValidChoice(value)) {
      return;
    }

    const current: MultipleChoiceQuestionAnswer = this.value$.getValue();

    if (this.isSelected(value)) {
      this.value$.next(current.filter((v) => v !== value));
    } else {
      this.value$.next([...current, value]);
    }
    this.pristine = false;
  }

  /**
   * Either select or de-select the provided option.
   */
  public onSingleSelect(value: string): void {
    if (!this.isValidChoice(value)) {
      return;
    }
    if (this.isSelected(value)) {
      this.value$.next([]);
    } else {
      this.value$.next([value]);
    }
    this.pristine = false;
  }

  /**
   * Check if the provided value is already selected.
   */
  public isSelected(value: string): boolean {
    return this.value$.getValue().includes(value);
  }

  /**
   * Check if the provided value is a valid choice.
   */
  private isValidChoice(value: string): boolean {
    return !!this._question.choices.find((element) => value === element.value);
  }
}
